import {
	ComboBox,
	DefaultButton,
	IComboBoxStyles,
	IDropdownOption,
	Label,
	Link,
	MessageBar,
	MessageBarType,
	Panel,
	PanelType,
	PrimaryButton,
	ScrollablePane,
	Separator,
	Stack,
	TextField,
	useTheme,
} from '@fluentui/react';
import {RegulatoryDetailsPageFormMode} from 'features/RegulatoryDocuments/RegDocDetailsPage/RegDocDetailsPage.types';
import React, {useEffect, useMemo, useState} from 'react';
import {
	useEditMatkonParagraphsFormEnumsTranslations,
	useEditMatkonParagraphsFormTranslations,
	useEditParagraphsFormTranslations,
} from '../EditParagraphsForm/editParagraphsFormTranslationHooks';
import {ELimitation, EOperator, EReferenceValue} from 'types';
import {
	MatkonApplicationsDocument,
	useCreateMatkonLimitsMutation,
} from './Matkon.generated';
import {
	ProviderThatEnablesGettingTooltipsFromContext,
	RegDocParagraphTooltipTranslationProvider,
} from 'features/localizedTooltips';
import {CreateMatkonApplicationDialog} from './CreateMatkonApplicationDialog';
import {LoadWrapper} from 'components/LoadWrapper';
import {
	DefaultFile,
	FileUpload,
} from 'components/hookForms/ControlledFileUpload';
import {ControlledDropdown, ControlledTagPicker} from 'components/hookForms';
import {renderParagraph} from 'components/EntityList/ColumnRenderers';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import ThreeStateToggle from 'components/ThreeStateToggle/ThreeStateToggle';
import {useMatkonsPropertiesData} from './hooks/useMatkonsPropertiesData';
import {ActionButton} from 'components';
import {useNotificationBar} from 'hooks';
import {useMatkonStyles} from '../hooks/useMatkonStyles';
import {MatkonInternalEntryDatePicker} from './components/MatkonInternalEntryDatePicker';
import {useMatkonFormUtils} from './hooks/useMatkonFormUtils';
import {MatkonLegalEntryDatePicker} from './components/MatkonLegalEntryDatePicker';
import {
	ExportMatkonParagraphsFormProps,
	MatkonFormType,
	Limit,
} from './MatkonTypes';

const EditMatkonParagraphsForm = (props: ExportMatkonParagraphsFormProps) => {
	const {
		handleCancelClick,
		closeForm,
		getIfIsLoading,
		mode,
		regulationNumber,
		selectedParagraphsCopy,
		selectedParagraphId,
		initialDeadlineLawDate,
	} = props;
	const {t: tDeprecated, i18n} = useEditParagraphsFormTranslations();
	const {t} = useEditMatkonParagraphsFormTranslations();
	const {t: tEnums} = useEditMatkonParagraphsFormEnumsTranslations();
	const {setMessage} = useNotificationBar();
	const [showMatkonDialog, setShowMatkonDialog] = useState(false);
	const [matkonErrorMessage, setMatkonErrorMessage] = useState<
		string | undefined
	>(undefined);

	const theme = useTheme();
	const {
		classNames,
		buttonStyles,
		scrollablePaneStyles,
		stackStyles,
		stackItemStyles,
		appHeaderStyles,
		tokens,
	} = useMatkonStyles(theme);

	const referenceValueOptions: IDropdownOption[] = Object.entries(
		EReferenceValue,
	).map(([key, value]) => ({
		key: value.replace(/_/g, ''),
		text: tEnums(key),
	}));
	const operatorOptions: IDropdownOption[] = Object.entries(EOperator).map(
		([key, value]) => ({
			key: value,
			text: key.replace(/([A-Z])/g, ' $1').trim(),
		}),
	);
	const comboBoxStyles: Partial<IComboBoxStyles> = {root: {width: '100%'}};

	const emptyLimit: Limit = {
		id: Date.now(), // For UI uniqueness, can also use a UUID
		iri: 'generatedIri', // TODO: generate appropriate IRI if needed
		application: {
			iri: '',
			createdBy: '',
			createdAt: undefined,
			modifiedAt: undefined,
			modifiedBy: '',
			description: '',
		}, // This will be set via the form
		deadlineInternal: new Date().toISOString(),
		deadlineLaw: initialDeadlineLawDate ?? new Date().toISOString(),
		expirationDate: new Date().toISOString(),
		attachment: undefined,
		imdsAssessmentOverwritten: false,
		limitations: [],
		matConComplianceEvaluationOverwritten: false,
		operator: EOperator.Equal,
		referenceValue: EReferenceValue.HomogeneousMaterial,
		unit: '',
		value: '0',
		applicationIri: undefined,
	};

	const {
		control: matkonControl,
		reset: matkonResetFields,
		watch: matkonWatch,
		setValue: setMatkonValue,
		setError,
		clearErrors,
	} = useForm<MatkonFormType>({
		reValidateMode: 'onSubmit',
		mode: 'all',
		defaultValues: {
			limits: [
				{
					id: 0,
					deadlineInternal: new Date().toISOString(),
					deadlineLaw: initialDeadlineLawDate ?? new Date().toISOString(),
					expirationDate: new Date().toISOString(),
					iri: '',
					limitations: [], // initially, no limitations are selected
					operator: EOperator.Equal,
					referenceValue: EReferenceValue.Component,
					unit: '',
					value: '0',
				},
			],
		},
	});
	const [limits, setLimits] = useState<Limit[]>([emptyLimit]);
	const [createMatkonLimitsMutation] = useCreateMatkonLimitsMutation();
	const {
		limits: matkonsLimits,
		applications,
		limitsTemplate,
		fetchLimits,
	} = useMatkonsPropertiesData({
		t: tDeprecated,
		regulationNumber,
		paragraphId: selectedParagraphId,
	});

	const addApplication = () => {
		const newLimits = [...limits, emptyLimit];
		setLimits(newLimits);
		matkonResetFields({limits: newLimits});
	};

	const removeApplication = (index: number) => {
		const newLimits = [...limits];
		newLimits.splice(index, 1);
		setLimits(newLimits);
		matkonResetFields({limits: newLimits});
	};

	const postLimit = async () => {
		const generalErrorMessage = `An error occured. Check the parameters, please.`; // to be customized later

		await createMatkonLimitsMutation({
			variables: {
				input: {
					traceParagraphId: selectedParagraphId,
					limits: limits.map(limit => {
						return {
							iri: 'test',
							applicationIri: limit.applicationIri ?? '',
							deadlineInternal: limit.deadlineInternal,
							deadlineLaw: limit.deadlineLaw,
							expirationDate: new Date(),
							attachment: limit.attachment,
							imdsAssessmentOverwritten: limit.imdsAssessmentOverwritten,
							limitations: limit.limitations,
							matConComplianceEvaluationOverwritten:
								limit.matConComplianceEvaluationOverwritten,
							operator: limit.operator,
							referenceValue: limit.referenceValue,
							unit: limit.unit,
							value: Number(limit.value),
							application: undefined,
						};
					}),
					traceRegulationNumber: regulationNumber,
				},
			},
			refetchQueries: [MatkonApplicationsDocument],
		})
			.then(ret => {
				console.log(`CreateMatkonLimits operation succeeded`);
				closeForm();
				setMessage({
					message: tDeprecated('MatkonLimitsUpdated'),
					timeout: 6 * 1000,
				});
			})
			.catch((e: Error) => {
				console.error(generalErrorMessage + ` ${e}`);
				setMatkonErrorMessage(generalErrorMessage);
			});
	};

	const confirmButtonDisabled = useMemo(() => {
		if (
			limits.some(
				limit =>
					!limit.value ||
					!limit.referenceValue ||
					!limit.operator ||
					!limit.attachment ||
					!limit.iri,
			)
		)
			return true;
		if (limits.some(limit => String(limit.value) !== '0' && !limit.unit))
			return true;
		if (limits.some(limit => limit.deadlineInternal > limit.deadlineLaw))
			return true;
		return false;
	}, [limits]);

	const onRenderMatKonFooterContent: any = () => (
		<form className={classNames.form}>
			<ActionButton
				disabled={confirmButtonDisabled}
				onClick={postLimit}
				styles={buttonStyles}
			>
				{tDeprecated('UpdateButton')}
			</ActionButton>

			<DefaultButton onClick={handleCancelClick}>
				{tDeprecated('CancelButton')}
			</DefaultButton>
		</form>
	);

	useEffect(() => {
		if (mode === RegulatoryDetailsPageFormMode.EditMatKon) {
			setLimits([emptyLimit]);
			matkonResetFields({
				limits: [emptyLimit],
			});
			fetchLimits();
		}
	}, [mode]);

	useEffect(() => {
		if (matkonsLimits.length > 0) {
			const fetchedLimits: Limit[] = matkonsLimits.flatMap(matkon =>
				matkon.limits.map((limit: Limit) => ({
					id: Date.now(),
					iri: limit.iri,
					applicationIri: limit.application?.iri,
					deadlineInternal:
						limit.deadlineInternal instanceof Date
							? limit.deadlineInternal
							: new Date(limit.deadlineInternal),
					deadlineLaw:
						limit.deadlineLaw instanceof Date
							? limit.deadlineLaw
							: new Date(limit.deadlineLaw),
					expirationDate: limit.expirationDate,
					attachment: limit.attachment,
					imdsAssessmentOverwritten: limit.imdsAssessmentOverwritten,
					limitations: limit.limitations,
					matConComplianceEvaluationOverwritten:
						limit.matConComplianceEvaluationOverwritten,
					operator: limit.operator,
					referenceValue: limit.referenceValue,
					unit: limit.unit,
					value: limit.value,
					application: limit.application,
				})),
			);

			setLimits(fetchedLimits);

			matkonResetFields({
				limits: fetchedLimits,
			});
		}
	}, [matkonsLimits, matkonResetFields]);

	useEffect(() => {
		if (
			mode === RegulatoryDetailsPageFormMode.EditMatKon &&
			selectedParagraphsCopy.length === 0
		) {
			console.error(
				`Matkon Edit form was open without any paragraph selected. We need to close the form, sorry.`,
			);
			closeForm();
			setMessage({
				message: tDeprecated('MatkonNoParagraphsError'),
				timeout: 8 * 1000,
				options: {
					messageBarType: MessageBarType.warning,
				},
			});
		}
	}, [selectedParagraphsCopy, mode]);

	const {mapToRow, mapToPhaseDateRow, mapToDateRow} = useMatkonFormUtils({
		classNames,
		i18n,
	});

	const [uploadedFiles, setUploadedFiles] = useState<DefaultFile[]>([]);

	return (
		<Panel
			isLightDismiss
			type={PanelType.extraLarge}
			isFooterAtBottom={true}
			onDismiss={handleCancelClick}
			isOpen={mode === RegulatoryDetailsPageFormMode.EditMatKon}
			closeButtonAriaLabel='Close'
			headerText={tDeprecated('EditMatKonMetadataHeader')}
			onRenderFooterContent={onRenderMatKonFooterContent}
		>
			{matkonErrorMessage && (
				<MessageBar
					messageBarType={MessageBarType.error}
					onDismiss={() => setMatkonErrorMessage(undefined)}
				>
					{matkonErrorMessage}
				</MessageBar>
			)}
			<LoadWrapper loading={getIfIsLoading()}>
				<Stack
					horizontal
					styles={{root: {height: '100%', position: 'relative'}}}
				>
					<ScrollablePane styles={scrollablePaneStyles}>
						<Stack tokens={{childrenGap: 8}} styles={{root: {marginTop: 4}}}>
							{selectedParagraphsCopy.map(p => (
								<div key={`${p.id}-paragraph`}>
									<div key={p.id}>{renderParagraph(p, tDeprecated)}</div>
									{selectedParagraphsCopy.length > 0 && (
										<div>
											{mapToDateRow(`${p.id}-new-types`, p.dateNewTypes)}
											{mapToDateRow(
												`${p.id}-new-registration`,
												p.dateNewRegistration,
											)}
											{mapToRow(p.id, 'keywords', p.keywords)}
											{mapToRow(p.id, 'driveVariants', p.driveVariants)}
											{mapToRow(p.id, 'vehicleCategories', p.vehicleCategories)}
											{mapToRow(p.id, 'categories', p.categories)}
											{mapToRow(p.id, 'tags', p.tags)}
											{mapToPhaseDateRow(`${p.id}-phase-in`, p.phaseIn)}
											{mapToPhaseDateRow(`${p.id}-phase-out`, p.phaseOut)}
										</div>
									)}
								</div>
							))}
						</Stack>
					</ScrollablePane>
					<Separator vertical />
					<Stack horizontal={false} tokens={tokens} styles={stackStyles}>
						<ScrollablePane styles={scrollablePaneStyles}>
							<RegDocParagraphTooltipTranslationProvider
								treatAsDottedFormFieldName
							>
								<ProviderThatEnablesGettingTooltipsFromContext>
									<Separator />
									{limits.map((limit, index) => {
										return (
											<div
												key={limit.id}
												className={`${classNames.separatorStyle} ${
													index % 2 === 1
														? classNames.oddApplication
														: classNames.evenApplication
												}`}
											>
												<Stack horizontal styles={appHeaderStyles}>
													<Stack.Item>
														<Label className={classNames.applicationHeader}>
															<b>{`Application ${index + 1}`}</b>
														</Label>
													</Stack.Item>
													<Stack.Item
														styles={{root: {marginLeft: '33% !important'}}}
													>
														<ActionButton
															onClick={async () => removeApplication(index)}
															styles={buttonStyles}
														>
															X
														</ActionButton>
													</Stack.Item>
												</Stack>
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<ControlledTagPicker
															className={classNames.restrictedForPicker}
															name={`limits[${index}].limitations`}
															label={t('RestrictionFor')}
															control={matkonControl}
															selectableItems={[
																ELimitation.Components,
																ELimitation.Mixtures,
																ELimitation.Substances,
															].map(item => {
																return {
																	key: item,
																	name: tEnums(item),
																};
															})}
															getKey={(item: any) =>
																typeof item === 'string' ? item : item.key
															}
															getName={(item: any) =>
																typeof item === 'string' ? item : item.name
															}
															performExtraActionOnChange={items => {
																const updatedLimits = [...limits];
																updatedLimits[index].limitations =
																	items?.map(
																		item => item.name as ELimitation,
																	) ?? [];
																setLimits(updatedLimits);
															}}
														/>
													</Stack.Item>
												</Stack>
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<ThreeStateToggle
															label={t('RelevantForIMDS')}
															value={limit.imdsAssessmentOverwritten}
															onChange={newValue => {
																setLimits(prevLimits => {
																	const newLimits = [...prevLimits];
																	newLimits[index].imdsAssessmentOverwritten =
																		newValue;
																	return newLimits;
																});
															}}
														/>
													</Stack.Item>
												</Stack>
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<ThreeStateToggle
															label={t('RelevantForCheck')}
															value={
																limit.matConComplianceEvaluationOverwritten
															}
															onChange={newValue => {
																setLimits(prevLimits => {
																	const newLimits = [...prevLimits];
																	newLimits[
																		index
																	].matConComplianceEvaluationOverwritten =
																		newValue;
																	return newLimits;
																});
															}}
														/>
													</Stack.Item>
												</Stack>
												<Separator />
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<MatkonLegalEntryDatePicker
															control={matkonControl}
															index={index}
															setLimits={setLimits}
															setError={setError}
															clearErrors={clearErrors}
															limit={limit}
														/>
													</Stack.Item>
													<Stack.Item grow={2} styles={stackItemStyles}>
														<MatkonInternalEntryDatePicker
															control={matkonControl}
															index={index}
															setLimits={setLimits}
															setError={setError}
															clearErrors={clearErrors}
															limit={limit}
															onDeleteClick={() => {
																setMatkonValue(
																	`limits.${index}.deadlineInternal`,
																	null,
																);
															}}
														/>
													</Stack.Item>
												</Stack>
												<Separator />
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<Controller
															name={`limits.${index}.value`}
															control={matkonControl}
															render={({field: {onChange, value}}) => (
																<TextField
																	required={true}
																	value={limit.value?.toString()}
																	onChange={(e, val) => {
																		const newValue = val || '';
																		// 20 instances is just any number to avoid Sonar DoS warning
																		if (
																			/^-?\d{0,20}\.?\d{0,20}$/.test(newValue)
																		) {
																			onChange(val);
																			setLimits(prevLimits => {
																				const newLimits = [...prevLimits];
																				newLimits[index].value = newValue;
																				return newLimits;
																			});

																			limit.value = newValue;
																		} else {
																			console.error(
																				`Number validation failed for ${newValue}`,
																			);
																		}
																	}}
																	label={tDeprecated('Limit')}
																	name='Limit'
																	type='text'
																/>
															)}
														/>
													</Stack.Item>
													<Stack.Item grow={2} styles={stackItemStyles}>
														<Controller
															name={`limits.${index}.operator`}
															control={matkonControl}
															render={({field: {onChange, value}}) => (
																<ComboBox
																	required={String(limit.value) !== '0'}
																	onChange={(e, val) => {
																		onChange(val?.key);
																		setLimits(prevLimits => {
																			const newLimits = [...prevLimits];

																			if (val?.key !== undefined) {
																				newLimits[index].operator =
																					val.key as EOperator;
																			}
																			return newLimits;
																		});
																	}}
																	label={tDeprecated('Operator')}
																	options={operatorOptions}
																	styles={comboBoxStyles}
																	autoComplete='on'
																	defaultSelectedKey={limit.operator}
																/>
															)}
														/>
													</Stack.Item>
													<Stack.Item grow={3} styles={stackItemStyles}>
														<Controller
															name={`limits.${index}.unit`}
															control={matkonControl}
															render={({field: {onChange, value}}) => (
																<TextField
																	required={String(limit.value) !== '0'}
																	value={limit.unit}
																	onChange={(e, val) => {
																		onChange(val);

																		setLimits(prevLimits => {
																			const newLimits = [...prevLimits];
																			newLimits[index].unit = val || '';
																			return newLimits;
																		});

																		limit.unit = val ?? '';
																	}}
																	label={tDeprecated('Unit')}
																	name='Unit'
																/>
															)}
														/>
													</Stack.Item>
													<Stack.Item grow={3} styles={stackItemStyles}>
														<Controller
															name={`limits.${index}.referenceValue`}
															control={matkonControl}
															render={({field: {onChange, value}}) => (
																<ComboBox
																	required={true}
																	onChange={(e, val) => {
																		onChange(val?.key);
																		setLimits(prevLimits => {
																			const newLimits = [...prevLimits];
																			if (val?.key !== undefined) {
																				newLimits[index].referenceValue =
																					val.key as EReferenceValue;
																			}
																			return newLimits;
																		});
																	}}
																	label={tDeprecated('ReferenceValue')}
																	options={referenceValueOptions}
																	styles={comboBoxStyles}
																	autoComplete='on'
																	defaultSelectedKey={limit.referenceValue}
																/>
															)}
														/>
													</Stack.Item>
												</Stack>
												<Separator />
												<Stack>
													<Label>
														{t('Premise')}
														<span className={classNames.application}>*</span>
														<PrimaryButton
															text=''
															iconProps={{iconName: 'Add'}}
															onClick={() => setShowMatkonDialog(true)}
														/>
													</Label>
												</Stack>
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<ControlledDropdown
															name={`limits.${index}.applicationIri`}
															control={matkonControl}
															options={applications}
															required={true}
															rules={{required: true}}
															styles={comboBoxStyles}
															defaultSelectedKey={limit.application?.iri}
															onChange={(e, val) => {
																setLimits(prevLimits => {
																	const newLimits = [...prevLimits];
																	newLimits[index].applicationIri = (val?.key ??
																		'') as string;
																	return newLimits;
																});
															}}
														/>
													</Stack.Item>
												</Stack>
												<Separator />
												<Label>{t('AddSubstances')}</Label>
												<Stack horizontal tokens={tokens}>
													<Stack.Item grow={1} styles={stackItemStyles}>
														<Link
															onClick={() => {
																const binary = atob(
																	limitsTemplate.base64Content,
																);
																const array = new Uint8Array(binary.length);
																for (let i = 0; i < binary.length; i++) {
																	array[i] = binary.charCodeAt(i);
																}
																const blob = new Blob([array], {
																	type: limitsTemplate.contentType,
																});

																// Create a link to download the file
																const link = document.createElement('a');
																link.href = window.URL.createObjectURL(blob);
																link.download = limitsTemplate.filename;
																link.click();
															}}
														>
															Export (Download file)
														</Link>
														<Separator />
														<FileUpload
															renderCategory={false}
															defaultFiles={
																uploadedFiles[index]
																	? [uploadedFiles[index]]
																	: undefined
															}
															onChange={files => {
																const newDocument =
																	files[files.length - 1]?.file;
																const uplFiles = [...uploadedFiles];
																if (newDocument) {
																	uplFiles[index] = {
																		name: newDocument.name,
																		blob: {
																			contentType: newDocument.type,
																			fileName: newDocument.name,
																			__typename: 'BlobRef',
																			uri: '',
																		},
																	};
																} else {
																	uplFiles.splice(index, 1);
																}
																setUploadedFiles(uplFiles);
																setLimits(prevLimits => {
																	const newLimits = [...prevLimits];
																	if (newDocument) {
																		newLimits[index].attachment = {
																			attachmentId: newDocument.name,
																			file: newDocument,
																		};
																	} else {
																		newLimits[index].attachment = undefined;
																	}
																	return newLimits;
																});
															}}
														/>
														<Label>
															<span>
																Export + Import von der Excel-Liste -&gt;
																Zugriff von PURE (Automatische Prüfung, welche
																CAS-Nr. neu sind, und diese in DB anlegen)
															</span>
														</Label>
													</Stack.Item>
												</Stack>
											</div>
										);
									})}
									<Separator />
									<PrimaryButton onClick={addApplication}>
										{t('AddApplication')}
									</PrimaryButton>
								</ProviderThatEnablesGettingTooltipsFromContext>
							</RegDocParagraphTooltipTranslationProvider>
						</ScrollablePane>
					</Stack>
				</Stack>
			</LoadWrapper>
			{showMatkonDialog && (
				<RegDocParagraphTooltipTranslationProvider>
					<CreateMatkonApplicationDialog
						showDialog={showMatkonDialog}
						setShowDialog={setShowMatkonDialog}
					/>
				</RegDocParagraphTooltipTranslationProvider>
			)}
		</Panel>
	);
};

export default EditMatkonParagraphsForm;
